import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/layout/Header';
import TopBar from './components/layout/TopBar';
import WebNavigation from './components/layout/WebNavigation';
import Footer from './components/layout/Footer';

import StandardHomePage from './pages/StandardHomePage';
import HomePage from './pages/HomePage';
import AcademicsPage from './pages/AcademicsPage';
import AdmissionPage from './pages/AdmissionPage';
import PlacementsPage from './pages/PlacementsPage';
import ResearchPage from './pages/ResearchPage';
import EventsPage from './pages/EventsPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import ChairmanMessagePage from './pages/ChairmanMessagePage';
import SecretaryMessagePage from './pages/SecretaryMessagePage';
import JointSecretaryMessagePage from './pages/JointSecretaryMessagePage';
import DirectorMessagePage from './pages/DirectorMessagePage';
import VisionMissionPage from './pages/VisionMissionPage';
import ContactUsPage from './pages/ContactUsPage';
import TermsConditionsPage from './pages/TermsConditionsPage';

// Admin imports
import { AuthProvider } from './contexts/AuthContext';
import AdminLogin from './components/admin/Login';
import AdminLayout from './components/admin/Layout';
import AdminDashboard from './components/admin/Dashboard';
import AdminPages from './components/admin/Pages';
import AdminPageEdit from './components/admin/PageEdit';
import AdminHomepageEdit from './components/admin/HomepageEdit';
import AdminVisionMissionEdit from './components/admin/VisionMissionEdit';
import AdminChairmanMessageEdit from './components/admin/ChairmanMessageEdit';
import AdminSecretaryMessageEdit from './components/admin/SecretaryMessageEdit';
import AdminJointSecretaryMessageEdit from './components/admin/JointSecretaryMessageEdit';
import AdminDirectorMessageEdit from './components/admin/DirectorMessageEdit';
import AdminApprovalsAffiliationsEdit from './components/admin/ApprovalsAffiliationsEdit';
import AdminPlacementsEdit from './components/admin/PlacementsEdit';
import AdminResearchEdit from './components/admin/ResearchEdit';
import AdminAnnouncements from './components/admin/Announcements';
import AdminAnnouncementEdit from './components/admin/AnnouncementEdit';
import AdminEvents from './components/admin/Events';
import AdminEventEdit from './components/admin/EventEdit';
import EventsPageEdit from './components/admin/EventsPageEdit';
import AdminContacts from './components/admin/Contacts';
import AdminAdmissionEnquiries from './components/admin/AdmissionEnquiries';
import AdminSettings from './components/admin/Settings';
import AdminUsers from './components/admin/Users';
import AdminGrievances from './components/admin/Grievances';
import AdminAcademicsEdit from './components/admin/AcademicsEdit';
import AdminAdmissionsEdit from './components/admin/AdminAdmissionsEdit';
import AdminSMTPSettings from './components/admin/SMTPSettings';
import AdminContactPageEdit from './components/admin/ContactPageEdit';

// Component to handle route-based tab highlighting
// Admin Protected Route Component
const AdminRoute = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isLoginRoute = location.pathname === '/admin/login';
  
  if (isAdminRoute && !isLoginRoute) {
    // For admin routes (except login), we need auth check in the component itself
    return children;
  }
  
  return children;
};

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const viewMode = 'brand'; // Default to brand OS view

  // Don't render main layout for admin routes
  if (isAdminRoute) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          
          {/* Website Page Editing Routes */}
          <Route path="pages/home" element={<AdminHomepageEdit />} />
          <Route path="pages/about" element={<AdminPageEdit pageType="about" />} />
          <Route path="pages/about/vision-mission" element={<AdminVisionMissionEdit />} />
          <Route path="pages/about/chairman-message" element={<AdminChairmanMessageEdit />} />
          <Route path="pages/about/secretary-message" element={<AdminSecretaryMessageEdit />} />
          <Route path="pages/about/joint-secretary-message" element={<AdminJointSecretaryMessageEdit />} />
          <Route path="pages/about/director-message" element={<AdminDirectorMessageEdit />} />
          <Route path="pages/about/approvals-affiliations" element={<AdminApprovalsAffiliationsEdit />} />
          <Route path="pages/academics" element={<AdminAcademicsEdit />} />
          <Route path="pages/admissions" element={<AdminAdmissionsEdit />} />
          <Route path="pages/placements" element={<AdminPlacementsEdit />} />
          <Route path="pages/research" element={<AdminResearchEdit />} />
          <Route path="pages/contact" element={<AdminContactPageEdit />} />
          
          {/* Legacy Page Routes */}
          <Route path="pages" element={<AdminPages />} />
          <Route path="pages/edit/:pageId" element={<AdminPageEdit />} />
          
          {/* Content Management Routes */}
          <Route path="announcements" element={<AdminAnnouncements />} />
          <Route path="announcements/new" element={<AdminAnnouncementEdit />} />
          <Route path="announcements/edit/:id" element={<AdminAnnouncementEdit />} />
          <Route path="events-page" element={<EventsPageEdit />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="events/new" element={<AdminEventEdit />} />
          <Route path="events/edit/:id" element={<AdminEventEdit />} />
          <Route path="contacts" element={<AdminContacts />} />
          <Route path="admission-enquiries" element={<AdminAdmissionEnquiries />} />
          
          {/* Admin Routes */}
          <Route path="users" element={<AdminUsers />} />
          <Route path="grievances" element={<AdminGrievances />} />
          <Route path="smtp-settings" element={<AdminSMTPSettings />} />
        </Route>
      </Routes>
    );
  }

  // Map routes to tab IDs
  const getActiveTabFromRoute = (pathname) => {
    const routeMap = {
      '/': 'home',
      '/about': 'about',
      '/academics': 'academics',
      '/admission': 'admission',
      '/placements': 'placements',
      '/research': 'research',
      '/events': 'events',
      '/announcements': 'announcements',
      '/about/chairman-message': 'about',
      '/about/secretary-message': 'about',
      '/about/joint-secretary-message': 'about',
      '/about/director-message': 'about',
      '/about/vision-mission': 'about',
      '/contact': 'contact'
    };
    return routeMap[pathname] || 'home';
  };

  const activeTab = getActiveTabFromRoute(location.pathname);

  const renderBrandOSView = () => (
    <div className="h-screen flex flex-col">
      <Header activeTab={activeTab} />
      <main className="flex-1 overflow-y-auto relative bg-surface">
        <div className="min-h-full flex flex-col">
          <div className="flex-1">
            <Routes>
              <Route path="/" element={<StandardHomePage />} />
              <Route path="/about" element={<HomePage />} />
              <Route path="/academics" element={<AcademicsPage />} />
              <Route path="/admission" element={<AdmissionPage />} />
              <Route path="/placements" element={<PlacementsPage />} />
              <Route path="/research" element={<ResearchPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/announcements" element={<AnnouncementsPage />} />
              <Route path="/about/chairman-message" element={<ChairmanMessagePage />} />
              <Route path="/about/secretary-message" element={<SecretaryMessagePage />} />
              <Route path="/about/joint-secretary-message" element={<JointSecretaryMessagePage />} />
              <Route path="/about/director-message" element={<DirectorMessagePage />} />
              <Route path="/about/vision-mission" element={<VisionMissionPage />} />
              <Route path="/contact" element={<ContactUsPage />} />
              <Route path="/terms" element={<TermsConditionsPage />} />
            </Routes>
          </div>
          <Footer />
        </div>
      </main>
    </div>
  );

  const renderWebsiteView = () => (
    <div className="min-h-screen bg-white">
      <TopBar />
      <WebNavigation />

      {/* Page Content */}
      <Routes>
        <Route path="/" element={<StandardHomePage />} />
        <Route path="/about" element={<HomePage />} />
        <Route path="/academics" element={<AcademicsPage />} />
        <Route path="/admission" element={<AdmissionPage />} />
        <Route path="/placements" element={<PlacementsPage />} />
        <Route path="/research" element={<ResearchPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/announcements" element={<AnnouncementsPage />} />
        <Route path="/about/chairman-message" element={<ChairmanMessagePage />} />
        <Route path="/about/secretary-message" element={<SecretaryMessagePage />} />
        <Route path="/about/joint-secretary-message" element={<JointSecretaryMessagePage />} />
        <Route path="/about/director-message" element={<DirectorMessagePage />} />
        <Route path="/about/vision-mission" element={<VisionMissionPage />} />
        <Route path="/contact" element={<ContactUsPage />} />
        <Route path="/terms" element={<TermsConditionsPage />} />
      </Routes>

      <Footer />
      
      {/* AI Assistant FAB */}
      <div className="fixed bottom-8 right-8 z-50">
        <button className="bg-slate text-white p-4 rounded-full shadow-float hover:bg-crimson transition transform hover:scale-110 flex items-center gap-2 group">
          <i className="fas fa-robot text-xl"></i>
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap text-sm font-bold">Ask FIT AI</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="App font-sans">
      {viewMode === 'brand' ? renderBrandOSView() : renderWebsiteView()}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
